import { useMemo } from "react";

import { BsMetaUtil } from "@bridgesplit/abf-react";
import { Column, MediaStats, StatProps } from "@bridgesplit/ui";
import { bsMath, formatPercent, formatStatNum, formatUsd, lamportsToUiAmount } from "@bridgesplit/utils";
import { COPY } from "app/constants";

import { useMarketContext, useQuotesContext } from "../common";
import { MARKET_DETAIL_BREAKPOINT, useMarketCapDetails } from "../util";
import { BorrowCapWarning } from "../../common";

export default function MarketStats() {
    const { token, borrowCap, principalStats: data, principalCollateralStats } = useMarketContext();
    const { collateralMints } = useQuotesContext();
    const symbol = BsMetaUtil.getSymbol(token);

    const principalUtilized = data?.principalStats.principalUtilized;

    const totalDeposits = data?.strategyStats.totalDeposits;
    const borrowCapDetails = useMarketCapDetails();

    const borrowedAmount = principalUtilized;

    const totalSupplied = bsMath.add(totalDeposits, borrowedAmount);
    const utilization = bsMath.div(borrowedAmount, totalSupplied);
    const collateralDepositsUsd = useMemo(() => {
        return getPrincipalCollateralStats({ principalCollateralStats, collateralMints });
    }, [principalCollateralStats, collateralMints]);

    const stats: StatProps[] = [
        { value: formatStatNum(totalSupplied, { symbol }), caption: "Supplied" },
        { value: formatStatNum(borrowedAmount, { symbol }), caption: "Borrowed" },
        {
            value: formatPercent(utilization),
            caption: "Utilization"
        },
        {
            value: formatStatNum(data?.principalStats.principalOriginated, { symbol }),
            caption: COPY.ORIGINATED_ALL_TIME_TERM
        },
        {
            caption: COPY.COLLATERAL_DEPOSITS_TERM,
            value: collateralDepositsUsd,
            hide: !collateralDepositsUsd
        },
        {
            value: (
                <BorrowCapWarning borrowCapWarning={borrowCapDetails?.warningMessage}>
                    {formatStatNum(lamportsToUiAmount(borrowCap?.global, token?.decimals), { symbol })}
                </BorrowCapWarning>
            ),
            caption: "Max outstanding borrows"
        }
    ];

    return (
        <Column data-testid="market-stats">
            <MediaStats
                query={MARKET_DETAIL_BREAKPOINT}
                variant="body1"
                loading={!data || borrowCap === undefined}
                stats={stats}
            />
        </Column>
    );
}

function getPrincipalCollateralStats({
    principalCollateralStats,
    collateralMints
}: {
    principalCollateralStats: Record<string, number> | undefined;
    collateralMints: string[];
}) {
    if (!principalCollateralStats) return undefined;

    let usdValue: number;
    if (!collateralMints || collateralMints.length === 0) {
        // return usd value of all collateral deposited against the principal if no collateral token is selected
        usdValue = Object.values(principalCollateralStats).reduce((acc, value) => acc + value, 0);
    } else {
        usdValue = collateralMints.reduce((acc, mint) => acc + (principalCollateralStats[mint] || 0), 0);
    }

    if (usdValue === 0) {
        return undefined;
    }

    return formatUsd(usdValue);
}
